import type { ReactNode } from "react";
import { useId } from "react";

import { cn } from "@/lib/cn";

const toneText = {
  up: "text-up",
  down: "text-down",
  warn: "text-warn",
  brand: "text-brand",
  muted: "text-muted",
} as const;

export type KpiTone = keyof typeof toneText;

const tileTone = {
  brand: "bg-brand-soft text-brand",
  up: "bg-up-soft text-up",
  down: "bg-down-soft text-down",
  warn: "bg-warn-soft text-warn",
  violet: "bg-tint-violet text-[#6d4fd8] dark:text-[#b39dff]",
  neutral: "bg-surface-2 text-muted",
} as const;

export type TileTone = keyof typeof tileTone;

/** A small rounded square holding an icon (KPI labels, list rows, menus). */
export function IconTile({
  tone = "brand",
  children,
  className,
}: {
  tone?: TileTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "grid size-8 shrink-0 place-items-center rounded-[9px] [&_svg]:size-4",
        tileTone[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * A line with a soft fill under it, scaled to its own min and max. Decorative:
 * the figures it draws are always stated in text next to it.
 */
export function Sparkline({
  values,
  tone = "brand",
  className,
}: {
  values: number[];
  tone?: "brand" | "up" | "down" | "warn";
  className?: string;
}) {
  const id = useId();
  if (values.length < 2) return null;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const pts = values
    .map(
      (v, i) =>
        `${(i / (values.length - 1)) * 100},${28 - ((v - lo) / (hi - lo || 1)) * 24 - 2}`,
    )
    .join(" ");
  const color = `var(--${tone})`;
  return (
    <svg
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
      aria-hidden
      className={cn("block h-9 w-full", className)}
    >
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.25" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon fill={`url(#${id})`} points={`0,30 ${pts} 100,30`} />
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.8"
        vectorEffect="non-scaling-stroke"
        points={pts}
      />
    </svg>
  );
}

/**
 * A summary box: label (with an optional icon tile), a large figure, a line
 * under it in a tone, and an optional sparkline or extra content at the bottom.
 */
export function Kpi({
  label,
  value,
  delta,
  deltaTone = "muted",
  icon,
  iconTone = "brand",
  spark,
  sparkTone,
  children,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  delta?: ReactNode;
  deltaTone?: KpiTone;
  icon?: ReactNode;
  iconTone?: TileTone;
  spark?: number[];
  sparkTone?: "brand" | "up" | "down" | "warn";
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "min-w-0 rounded-card border border-border bg-surface px-[18px] py-4 shadow-card",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2 text-[12.5px] text-muted">
        {icon ? <IconTile tone={iconTone}>{icon}</IconTile> : null}
        <span className="truncate">{label}</span>
      </div>
      <div className="num mt-1.5 truncate font-display text-[1.875rem] leading-[1.1] font-bold text-fg">
        {value}
      </div>
      {delta ? (
        <div
          className={cn(
            "num mt-0.5 truncate text-[12.5px] font-semibold",
            toneText[deltaTone],
          )}
        >
          {delta}
        </div>
      ) : null}
      {spark ? (
        <Sparkline
          values={spark}
          tone={sparkTone ?? "brand"}
          className="mt-2.5"
        />
      ) : null}
      {children}
    </div>
  );
}

const GRID =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Cpath d='M40 0H0v40' fill='none' stroke='white' stroke-opacity='.06'/%3E%3C/svg%3E\")";

/**
 * The page-top banner on dashboards: a deep blue-violet gradient with a fine
 * grid, white text. Children lay themselves out.
 */
export function Hero({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[20px] px-6 py-[22px] text-white shadow-lift",
        "bg-[radial-gradient(120%_140%_at_100%_0%,#6d4fd8_0%,transparent_55%),linear-gradient(135deg,#123ad0_0%,#1f56e0_45%,#3a2fb8_100%)]",
        className,
      )}
    >
      <div
        aria-hidden
        className="absolute inset-0"
        style={{ backgroundImage: GRID }}
      />
      <div className="relative">{children}</div>
    </div>
  );
}

/** A frosted box inside a Hero (a figure on the banner). */
export function HeroStat({
  label,
  value,
}: {
  label: ReactNode;
  value: ReactNode;
}) {
  return (
    <div className="rounded-[14px] border border-white/20 bg-white/[0.12] px-3.5 py-3 backdrop-blur-sm">
      <div className="text-xs text-[#c9d6ff]">{label}</div>
      <div className="num font-display text-2xl leading-tight font-bold">
        {value}
      </div>
    </div>
  );
}
